import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from '../../common/Toast/toast';
import { useParams, useNavigate } from 'react-router-dom';
import { getModulesToApps } from '../../../../services/ModulesToAppService.js';
import { createappstoroles, getappstorolesById, updateappstoroles } from '../../../../services/AppToRole.js';
import '../../common/SettingsStepForm.css';

/* Step one's rules on their own, and the whole form's built from them.
   Split so that "may Continue?" is answered by the same rules that decide
   "may Save?" -- the button used to be gated on a hand-written
   "both fields are non-empty", which is a second, quieter copy of the
   schema that drifts the moment a rule is added here. */
const stepOneSchema = Yup.object().shape({
  label: Yup.string().trim().required('Label is required'),
  value: Yup.string().trim().required('Value is required'),
});

const validationSchema = stepOneSchema.shape({
  profileIds: Yup.array().min(1, 'At least one role is required'),
});

/* ── The two steps ──────────────────────────────────────────────────
   The form asks two unrelated questions and the page put both on one
   screen, where the second -- a wall of switches -- became the whole
   thing and the two text fields above it read as an afterthought. Same
   split, and the same machinery, AddAttendance uses.

   The dialog titles itself from this, through onStepChange.
   ------------------------------------------------------------------ */
const STEP_COPY = [
  {
    title: 'What is this mapping called?',
    hint: 'A name for the entry, and the key permission checks are written against.',
  },
  {
    title: 'Which module mappings does it cover?',
    hint: 'Tick every module mapping this entry grants.',
  },
];

const STEP_LABELS = ['Name', 'Mappings'];

// The rule that turns a label into its key. Named because three places
// have to agree on it: the label's onChange, the "use the generated one"
// link, and the check for whether the key is still the derived one.
const slugify = (label) => String(label || '').toLowerCase().trim().replace(/\s+/g, '-');

const idOf = (role) => String(role?._id || role?.id || '');

const TickIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const WarnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

/* The palette's own tokens rather than a second theme of this
   screen's own. These were literal oranges, so these pages and their
   popups stayed orange whichever palette was chosen while the app
   around them moved. ThemeContext writes the --pg-* custom properties
   onto the document, and a custom property works in an inline style
   exactly as it does in a stylesheet, so naming them here is the whole
   of the fix.

   Ink is deliberately not a palette value (see css/theme/palettes.css):
   it holds its contrast under every palette, so it stays literal. */
const THEME = {
  primary: 'var(--pg-accent)',
  primaryDark: 'var(--pg-accent-hover)',
  cardBg: 'var(--pg-card)',
  border: 'var(--pg-line)',
};

const FilterSection = ({ title, children }) => (
  <div className="filter-section mb-4 p-4 bg-dark-2" style={{
    borderRadius: '15px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    // background: THEME.cardBg,
    border: `1px solid ${THEME.border}`,
  }}>
    <h5 className="mb-3" style={{
      borderBottom: `2px solid ${THEME.primary}`,
      paddingBottom: '8px',
      color: THEME.primaryDark,
    }}>
      {title}
    </h5>
    {children}
  </div>
);

function AddRolesToApps({
  // Shown inside a dialog rather than as a page: the component drops its
  // own back-link and toast host, since the dialog and the list supply
  // them. The card chrome around it is flattened by SettingsFormModal.css.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
  // Told the current step's question, so the dialog can title itself.
  onStepChange,
}) {
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const navigate = useNavigate();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate('/setting/list-app-to-role');
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate('/setting/list-app-to-role');
  };
  const [roles, setRoles] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState({
    label: '',
    value: '',
    profileIds: [],
  });

  /* ── Steps ────────────────────────────────────────────────────────
     `step` is the one being asked for; `shownStep` is the one on
     screen, which trails it by one exit. Keying the stage on `step`
     alone swapped the content in a single frame -- the old step
     vanished with no exit while the new one animated in from nothing,
     which is what reads as a jump. Same pair AddAttendance keeps.
     ---------------------------------------------------------------- */
  const [step, setStep] = useState(1);
  const [shownStep, setShownStep] = useState(1);
  const [stepPhase, setStepPhase] = useState('in');
  // What the picker's search box holds. Lives here rather than in the
  // step so it survives a trip back to step one.
  const [pickQuery, setPickQuery] = useState('');

  useEffect(() => {
    if (shownStep === step) return undefined;
    setStepPhase('out');
    const t = setTimeout(() => {
      setShownStep(step);
      setStepPhase('in');
    }, 160);
    return () => clearTimeout(t);
  }, [step, shownStep]);

  useEffect(() => {
    if (onStepChange) onStepChange(STEP_COPY[step - 1], step);
    // onStepChange is the dialog's setState and is stable enough; adding
    // it here would re-report on every render of the list behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch roles from ModulesToAppService
        const rolesRes = await getModulesToApps(1000, 1, '');
        setRoles(rolesRes.data || []);
// console.log("Fetched roles:", rolesRes.data);
        // If in edit mode, fetch the existing mapping
        if (id) {
          const mappingRes = await getappstorolesById(id);
          const mapping = mappingRes.data;
          
          setInitialValues({
            label: mapping.label || '',
            value: mapping.value || '',
            profileIds: mapping.profileIds ? mapping.profileIds.split(',') : [],
          });
          setIsEditMode(true);
        }
      } catch (error) {
        toast.error('Failed to fetch data');
        console.error('Error:', error);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        label: values.label,
        value: values.value,
        profileIds: values.profileIds.join(','), // Convert array to comma-separated string
      };

      if (isEditMode) {
        await updateappstoroles(id, payload);
        toast.success('App to roles mapping updated successfully!');
      } else {
        await createappstoroles(payload);
        toast.success('App to roles mapping created successfully!');
        resetForm();
      }
      
      finish();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error submitting data');
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── The dialog build ───────────────────────────────────────────────
     Two steps, the shape AddAttendance uses. The stepper, the stage and
     its in/out transition are the same `att-*` classes, so the two
     dialogs step identically; everything specific to this form is in
     common/SettingsStepForm.css.
     ------------------------------------------------------------------ */
  if (embedded) {
    return (
      <div className="sf">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            setFieldValue,
            isSubmitting,
            errors,
            touched,
            setFieldTouched,
            submitForm,
          }) => {
            const derived = slugify(values.label);
            // The key still matches what the label would generate, so it
            // can go on tracking it. Once it does not, the label stops
            // overwriting it -- a typed key is the reader's, not ours.
            const isAuto = values.value === derived;
            const labelBad = touched.label && errors.label;
            const valueBad = touched.value && errors.value;
            const picksBad = touched.profileIds && errors.profileIds;

            const selected = new Set(values.profileIds.map(String));
            const shown = pickQuery.trim()
              ? roles.filter((r) =>
                  String(r.label || '').toLowerCase().includes(pickQuery.trim().toLowerCase()),
                )
              : roles;

            const toggle = (rid) => {
              setFieldTouched('profileIds', true, false);
              setFieldValue(
                'profileIds',
                selected.has(rid)
                  ? values.profileIds.filter((v) => String(v) !== rid)
                  : [...values.profileIds, rid],
              );
            };

            // Bulk actions act on what the search is showing, not on the
            // whole list -- "Select all" under a filter that hides most of
            // it, silently ticking the hidden ones, is a trap.
            const shownIds = shown.map(idOf);
            const allShownOn = shownIds.length > 0 && shownIds.every((rid) => selected.has(rid));

            const selectShown = () => {
              setFieldTouched('profileIds', true, false);
              const merged = new Set([...values.profileIds.map(String), ...shownIds]);
              setFieldValue('profileIds', [...merged]);
            };

            const clearShown = () => {
              setFieldTouched('profileIds', true, false);
              const drop = new Set(shownIds);
              setFieldValue(
                'profileIds',
                values.profileIds.filter((v) => !drop.has(String(v))),
              );
            };

            // Step one is answered when its own fields pass their own
            // rules -- the same object the full schema is built from, so
            // there is no second copy of them here to fall out of step.
            const stepOneOk = stepOneSchema.isValidSync({
              label: values.label,
              value: values.value,
            });

            const goToTwo = () => {
              // Mark them touched so a field that fails shows why, on the
              // off chance this is reached with the button enabled --
              // Enter in a field can call it too.
              setFieldTouched('label', true, true);
              setFieldTouched('value', true, true);
              if (stepOneOk) setStep(2);
            };

            return (
              <Form
                noValidate
                onSubmit={(e) => {
                  // Nothing in here is type="submit", so the only thing that
                  // reaches this is Enter pressed inside a field. On step one
                  // that key means "continue" -- it must not save a form
                  // whose second question has not been asked yet.
                  e.preventDefault();
                  if (step !== 2) {
                    goToTwo();
                    return;
                  }
                  setFieldTouched('profileIds', true, true);
                  if (!stepOneOk) {
                    setStep(1);
                    return;
                  }
                  submitForm();
                }}
              >
                <ol className="att-steps">
                  {STEP_LABELS.map((label, i) => {
                    const n = i + 1;
                    const state = n === step ? 'is-now' : n < step ? 'is-done' : '';
                    return (
                      <li className={`att-step-tab ${state}`.trim()} key={label}>
                        <button
                          type="button"
                          // Adding: only backwards, a step ahead has not been
                          // answered yet. Editing: every step is filled, so any tab opens.
                          disabled={n === step || (!isEditMode && n > step)}
                          onClick={() => setStep(n)}
                        >
                          <span className="att-step-n">{n}</span>
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ol>

                {/* Painted from shownStep, not step -- see the comment on
                    the transition effect above. */}
                <div className={`att-stage is-${stepPhase}`}>
                  <div className="att-stage-inner" key={shownStep}>
                    {shownStep === 1 && (
                      <section className="att-step">
                        <p className="sf-step-note">
                          The name is what you will recognise this entry by; the
                          key is what the code checks.
                        </p>

                        <div className="sf-field">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="sf-label">
                              Label<span className="sf-req">*</span>
                            </label>
                          </div>
                          <input
                            id="sf-label"
                            type="text"
                            className={`sf-input${labelBad ? ' sf-input--bad' : ''}`}
                            value={values.label}
                            placeholder="Reports, Billing, Attendance…"
                            autoComplete="off"
                            onChange={(e) => {
                              const next = e.target.value;
                              setFieldValue('label', next);
                              // Only while the key is still the generated one.
                              if (isAuto) setFieldValue('value', slugify(next));
                            }}
                            onBlur={() => setFieldTouched('label', true, true)}
                          />
                          {labelBad ? (
                            <p className="sf-error"><WarnIcon />{errors.label}</p>
                          ) : (
                            <p className="sf-hint">
                              How this mapping reads wherever it is listed.
                            </p>
                          )}
                        </div>

                        <div className="sf-field sf-derived">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="atr-value">
                              Value<span className="sf-req">*</span>
                              {isAuto ? (
                                <span className="sf-state sf-state--auto">From label</span>
                              ) : (
                                <>
                                  <span className="sf-state sf-state--custom">Edited</span>
                                  {derived && (
                                    <button
                                      type="button"
                                      className="sf-relink"
                                      onClick={() => setFieldValue('value', derived)}
                                    >
                                      use {derived}
                                    </button>
                                  )}
                                </>
                              )}
                            </label>
                          </div>
                          <input
                            id="atr-value"
                            type="text"
                            className={`sf-input sf-input--slug${valueBad ? ' sf-input--bad' : ''}`}
                            value={values.value}
                            placeholder="reports"
                            autoComplete="off"
                            spellCheck="false"
                            onChange={(e) =>
                              setFieldValue('value', e.target.value.replace(/\s+/g, '-'))
                            }
                            onBlur={() => setFieldTouched('value', true, true)}
                          />
                          {valueBad ? (
                            <p className="sf-error"><WarnIcon />{errors.value}</p>
                          ) : (
                            <p className="sf-hint">
                              The key permission checks are written against. Changing
                              it on an entry already in use breaks those checks.
                            </p>
                          )}
                        </div>
                      </section>
                    )}

                    {shownStep === 2 && (
                      <section className="att-step">
                        <p className="sf-step-note">
                          Every module mapping ticked here is granted by this entry.
                        </p>

                        <div className="sf-pick-bar">
                          <div className="sf-search">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                              <circle cx="11" cy="11" r="7" />
                              <path d="m20 20-3.5-3.5" />
                            </svg>
                            <input
                              type="text"
                              className="sf-search-input"
                              value={pickQuery}
                              placeholder="Search module mappings…"
                              autoComplete="off"
                              onChange={(e) => setPickQuery(e.target.value)}
                            />
                          </div>

                          <button
                            type="button"
                            className="sf-bulk"
                            onClick={allShownOn ? clearShown : selectShown}
                            disabled={shownIds.length === 0}
                          >
                            {allShownOn ? 'Clear' : 'Select'}
                            {pickQuery.trim() ? ' shown' : ' all'}
                          </button>
                        </div>

                        <p className="sf-tally">
                          <b>{values.profileIds.length}</b> of {roles.length} selected
                          {pickQuery.trim() && ` · ${shown.length} shown`}
                        </p>

                        {shown.length === 0 ? (
                          <div className="sf-empty">
                            {roles.length === 0
                              ? 'No module mappings exist yet.'
                              : `Nothing matches “${pickQuery.trim()}”.`}
                          </div>
                        ) : (
                          <ul className="sf-picklist">
                            {shown.map((role) => {
                              const rid = idOf(role);
                              const on = selected.has(rid);
                              return (
                                <li key={rid}>
                                  <button
                                    type="button"
                                    className={`sf-pick${on ? ' is-on' : ''}`}
                                    aria-pressed={on}
                                    onClick={() => toggle(rid)}
                                  >
                                    <span className="sf-box" aria-hidden="true">
                                      <TickIcon />
                                    </span>
                                    <span className="sf-pick-name">{role.label}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {picksBad && (
                          <p className="sf-error"><WarnIcon />{errors.profileIds}</p>
                        )}
                      </section>
                    )}
                  </div>
                </div>

                <footer className="sf-foot">
                  {/* Keyed for the same reason as the pair on the right:
                      two buttons at one position are otherwise reconciled
                      onto a single DOM node. */}
                  {step > 1 ? (
                    <button key="back" type="button" className="sf-btn" onClick={() => setStep(1)}>
                      Back
                    </button>
                  ) : (
                    <button key="cancel" type="button" className="sf-btn" onClick={leave}>
                      Cancel
                    </button>
                  )}

                  <span className="sf-foot-gap" />

                  {/* Neither of these is type="submit", and the keys keep
                      them as two separate elements.

                      Continue used to save the record outright. The two
                      buttons sit at the same position in the tree, so React
                      reconciled them onto ONE DOM node and just changed its
                      attributes. Clicking Continue ran goToTwo, React
                      flushed step=2 synchronously -- discrete event -- and
                      by the time the browser got to the click's DEFAULT
                      action, the very node that had been clicked was now
                      type="submit" inside a form. So it submitted, the
                      form's onSubmit read the already-updated step as 2,
                      and the mapping was saved from step one.

                      A button with no submit type has no default action to
                      inherit, which is what closes that off for good. */}
                  {step === 1 ? (
                    <button
                      key="continue"
                      type="button"
                      className="sf-btn sf-btn--main"
                      onClick={goToTwo}
                      disabled={!stepOneOk}
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      key="save"
                      type="button"
                      className="sf-btn sf-btn--main"
                      disabled={isSubmitting || values.profileIds.length === 0}
                      onClick={() => {
                        setFieldTouched('profileIds', true, true);
                        // Saving with a step-one field invalid would fail
                        // validation silently -- its error lives on a step
                        // that is not on screen. Go back and show it.
                        if (!stepOneOk) {
                          setStep(1);
                          return;
                        }
                        submitForm();
                      }}
                    >
                      {isSubmitting && <span className="sf-spin" aria-hidden="true" />}
                      {isSubmitting
                        ? isEditMode ? 'Updating…' : 'Saving…'
                        : isEditMode ? 'Update mapping' : 'Create mapping'}
                    </button>
                  )}
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Page-only: the list behind the dialog already has a toast host,
          and two would show every message twice. */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '15px', overflow: 'hidden' }}>
        <Card.Body className='bg-dark'>
          <FilterSection title={isEditMode ? "Edit App to Roles Mapping" : "Add App to Roles Mapping"}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {(formik) => {
                const { values, setFieldValue, isSubmitting, errors, touched } = formik;

                const toggleRole = (roleId) => {
                  if (values.profileIds.includes(roleId)) {
                    setFieldValue(
                      'profileIds',
                      values.profileIds.filter((v) => v !== roleId)
                    );
                  } else {
                    setFieldValue('profileIds', [...values.profileIds, roleId]);
                  }
                };

                const selectAll = () => setFieldValue('profileIds', roles.map(r => r._id || r.id));
                const clearAll = () => setFieldValue('profileIds', []);

                return (
                  <Form onSubmit={formik.handleSubmit}>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="formLabel">
                          <Form.Label style={{ color: THEME.primaryDark }}>
                            Label <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="label"
                            value={values.label}
                            onChange={(e) => {
                              const label = e.target.value;
                              const processed = label.toLowerCase().replace(/\s+/g, '-');
                              setFieldValue('label', label);
                              setFieldValue('value', processed);
                            }}
                            placeholder="Enter label"
                            isInvalid={touched.label && errors.label}
                          />
                          <ErrorMessage name="label" component="div" className="text-danger" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="formValue">
                          <Form.Label style={{ color: THEME.primaryDark }}>
                            Value <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="value"
                            value={values.value}
                            onChange={(e) =>
                              setFieldValue('value', e.target.value.replace(/\s+/g, '-'))
                            }
                            placeholder="Enter value"
                            isInvalid={touched.value && errors.value}
                          />
                          <ErrorMessage name="value" component="div" className="text-danger" />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group controlId="formRolesIds">
                          <Form.Label style={{ color: THEME.primaryDark }}>
                            Module Mapping <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="mb-2 d-flex gap-2">
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={selectAll}
                              style={{ borderRadius: '20px', fontWeight: 500 }}
                              type="button"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="outline-dark"
                              size="sm"
                              onClick={clearAll}
                              style={{ borderRadius: '20px', fontWeight: 500 }}
                              type="button"
                            >
                              Clear All
                            </Button>
                          </div>
                          <div className="d-flex flex-wrap gap-3">
                            {roles.map((role) => (
                              <div
                                key={role._id || role.id}
                                className={`d-flex align-items-center px-3 py-2 rounded border bg-white text-blackk`}
                                style={{
                                  minWidth: 220,
                                  marginBottom: 8,
                                  borderWidth: 2,
                                  cursor: 'pointer',
                                  transition: 'background 0.2s',
                                }}
                                onClick={() => toggleRole(role._id || role.id)}
                              >
                                <Form.Check
                                  type="switch"
                                  id={`role-switch-${role._id || role.id}`}
                                  checked={values.profileIds.includes(role._id || role.id)}
                                  onChange={() => toggleRole(role._id || role.id)}
                                  className="me-2"
                                  style={{ pointerEvents: 'none' }}
                                />
                                <span style={{ fontWeight: 500, color: '#444' }}>{role.label}</span>
                              </div>
                            ))}
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                            Select roles that will be assigned to this app
                          </div>
                          <ErrorMessage name="profileIds" component="div" className="text-danger" />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-3 mt-4">
                      <Button
                        variant="secondary"
                        onClick={leave}
                        style={{
                          borderRadius: '30px',
                          padding: '8px 30px',
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="success"
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          backgroundColor: THEME.primary,
                          borderColor: THEME.primary,
                          borderRadius: '30px',
                          padding: '8px 30px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        }}
                      >
                        {isSubmitting ? 'Processing...' : isEditMode ? 'Update' : 'Save'}
                      </Button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </FilterSection>
        </Card.Body>
      </Card>
    </div>
  );
}

export default AddRolesToApps;